import { WaveSelector } from '../components/WaveSelector.js';
import { themeManager } from '../themes/ThemeManager.js';
import { PresetSelector } from '../components/PresetSelector.js';
import { ARP2600_PRESETS } from '../../state/InstrumentPresets.js';

/**
 * ARP 2600 Panel - Semi-modular synthesizer UI
 * Follows the same conventions as Jupiter8Panel
 */
export class ARP2600Panel {
    constructor(arp2600, sequencer, store) {
        this.synth = arp2600;
        this.sequencer = sequencer;
        this.store = store;
        this.panelId = 'arp2600';
        this.container = null;
        this.components = {
            knobs: {},
            selects: {},
            stepButtons: [],
            waveSelectors: {}
        };
    }

    isCollapsed() {
        return this.store?.isPanelCollapsed(this.panelId) || false;
    }

    render(containerId) {
        this.container = document.getElementById(containerId);
        if (!this.container) return;

        const isCollapsed = this.isCollapsed();
        const isPowered = this.store.getPowerState('arp2600');

        this.synth.setPower(isPowered);

        this.container.innerHTML = `
            <div class="machine arp2600 ${isCollapsed ? 'collapsed' : ''} ${!isPowered ? 'powered-off' : ''}" id="arp2600_machine">
                <div class="machine-title">
                    <button class="collapse-btn" id="arp2600_collapse" title="Réduire/Agrandir">${isCollapsed ? '▶' : '▼'}</button>
                    ARP 2600 <span class="brand">Semi-Modular Synthesizer</span>
                    <div id="arp2600_presets"></div>
                    <button class="power-btn ${isPowered ? 'on' : ''}" title="Power On/Off">⏻</button>
                </div>

                <div class="machine-content ${isCollapsed ? 'hidden' : ''}" id="arp2600_content">
                <div class="arp2600-controls">
                    <!-- VCO Section -->
                    <div class="control-section">
                        <div class="section-label">VCO 1</div>
                        <div class="control-row">
                            <div id="arp_vco1_wave"></div>
                            <div id="arp_vco1_fine"></div>
                            <div id="arp_vco1_level"></div>
                        </div>
                    </div>

                    <div class="control-section">
                        <div class="section-label">VCO 2</div>
                        <div class="control-row">
                            <div id="arp_vco2_wave"></div>
                            <div id="arp_vco2_fine"></div>
                            <div id="arp_vco2_level"></div>
                        </div>
                    </div>

                    <div class="control-section">
                        <div class="section-label">VCO 3</div>
                        <div class="control-row">
                            <div id="arp_vco3_wave"></div>
                            <div id="arp_vco3_fine"></div>
                            <div id="arp_vco3_level"></div>
                        </div>
                    </div>

                    <div class="control-section">
                        <div class="section-label">MIXER</div>
                        <div class="control-row">
                            <div id="arp_noise_level"></div>
                            <div id="arp_ring_level"></div>
                        </div>
                    </div>

                    <!-- Filter Section -->
                    <div class="control-section">
                        <div class="section-label">VCF</div>
                        <div class="control-row">
                            <div id="arp_filter_cutoff"></div>
                            <div id="arp_filter_res"></div>
                            <div id="arp_filter_env"></div>
                        </div>
                    </div>

                    <!-- Envelope 1 (Filter) -->
                    <div class="control-section">
                        <div class="section-label">ENV 1 (FILTER)</div>
                        <div class="control-row">
                            <div id="arp_env1_a"></div>
                            <div id="arp_env1_d"></div>
                            <div id="arp_env1_s"></div>
                            <div id="arp_env1_r"></div>
                        </div>
                    </div>

                    <!-- Envelope 2 (VCA) -->
                    <div class="control-section">
                        <div class="section-label">ENV 2 (VCA)</div>
                        <div class="control-row">
                            <div id="arp_env2_a"></div>
                            <div id="arp_env2_d"></div>
                            <div id="arp_env2_s"></div>
                            <div id="arp_env2_r"></div>
                        </div>
                    </div>

                    <!-- LFO -->
                    <div class="control-section">
                        <div class="section-label">LFO</div>
                        <div class="control-row">
                            <div id="arp_lfo_wave"></div>
                            <div id="arp_lfo_rate"></div>
                            <div id="arp_lfo_vco"></div>
                            <div id="arp_lfo_vcf"></div>
                            <div id="arp_lfo_vca"></div>
                        </div>
                    </div>

                    <!-- Master -->
                    <div class="control-section">
                        <div class="section-label">MASTER</div>
                        <div class="control-row">
                            <div id="arp_volume"></div>
                            <div id="arp_glide"></div>
                        </div>
                    </div>
                </div>

                <!-- Sequencer - Same structure as Jupiter-8 -->
                <div class="sequencer machine-sequencer">
                    <div class="step-markers">
                        ${[...Array(16)].map((_, i) => `<span class="step-marker">${i + 1}</span>`).join('')}
                    </div>
                    <div class="seq-row">
                        <span class="seq-label">GATE</span>
                        <div class="steps" id="arp2600_gates"></div>
                    </div>
                    <div class="seq-row">
                        <span class="seq-label">NOTE</span>
                        <div class="note-row" id="arp2600_notes"></div>
                    </div>
                </div>
                </div><!-- /machine-content -->
            </div>
        `;

        this.setupCollapseButton();
        this.setupPowerButton();

        if (!isCollapsed) {
            this.createControls();
            this.createSequencer();
        }
    }

    setupCollapseButton() {
        const collapseBtn = document.getElementById('arp2600_collapse');
        if (!collapseBtn) return;

        collapseBtn.addEventListener('click', () => {
            this.store.togglePanelCollapsed(this.panelId);
            this.destroy();
            this.render('arp2600Container');
        });
    }

    setupPowerButton() {
        const machine = document.getElementById('arp2600_machine');
        if (!machine) return;

        const powerBtn = machine.querySelector('.power-btn');
        if (powerBtn) {
            powerBtn.addEventListener('click', () => {
                const newState = !this.synth.isPowered;
                this.synth.setPower(newState);
                this.store.setPowerState('arp2600', newState);
                powerBtn.classList.toggle('on', newState);
                machine.classList.toggle('powered-off', !newState);
            });
        }
    }

    createControls() {
        const params = this.synth.getParams();

        // VCO 1
        this.components.waveSelectors.vco1 = new WaveSelector(
            document.getElementById('arp_vco1_wave'),
            {
                waveforms: [
                    { value: 'sawtooth', label: 'SAW' },
                    { value: 'square', label: 'SQR' },
                    { value: 'triangle', label: 'TRI' },
                    { value: 'sine', label: 'SIN' }
                ],
                value: params.vco1Wave
            }
        ).onChange(w => { this.synth.setVco1Wave(w); this._saveParams(); });

        this.components.knobs.vco1Fine = themeManager.createKnob(
            document.getElementById('arp_vco1_fine'),
            { min: -100, max: 100, value: params.vco1Fine, label: 'FINE', step: 1, unit: 'ct' }
        ).onChange(v => { this.synth.setVco1Fine(v); this._saveParams(); });

        this.components.knobs.vco1Level = themeManager.createKnob(
            document.getElementById('arp_vco1_level'),
            { min: 0, max: 1, value: params.vco1Level, label: 'LEVEL', step: 0.01 }
        ).onChange(v => { this.synth.setVco1Level(v); this._saveParams(); });

        // VCO 2
        this.components.waveSelectors.vco2 = new WaveSelector(
            document.getElementById('arp_vco2_wave'),
            {
                waveforms: [
                    { value: 'sawtooth', label: 'SAW' },
                    { value: 'square', label: 'SQR' },
                    { value: 'triangle', label: 'TRI' },
                    { value: 'sine', label: 'SIN' }
                ],
                value: params.vco2Wave
            }
        ).onChange(w => { this.synth.setVco2Wave(w); this._saveParams(); });

        this.components.knobs.vco2Fine = themeManager.createKnob(
            document.getElementById('arp_vco2_fine'),
            { min: -100, max: 100, value: params.vco2Fine, label: 'FINE', step: 1, unit: 'ct' }
        ).onChange(v => { this.synth.setVco2Fine(v); this._saveParams(); });

        this.components.knobs.vco2Level = themeManager.createKnob(
            document.getElementById('arp_vco2_level'),
            { min: 0, max: 1, value: params.vco2Level, label: 'LEVEL', step: 0.01 }
        ).onChange(v => { this.synth.setVco2Level(v); this._saveParams(); });

        // VCO 3
        this.components.waveSelectors.vco3 = new WaveSelector(
            document.getElementById('arp_vco3_wave'),
            {
                waveforms: [
                    { value: 'sawtooth', label: 'SAW' },
                    { value: 'square', label: 'SQR' },
                    { value: 'triangle', label: 'TRI' },
                    { value: 'sine', label: 'SIN' }
                ],
                value: params.vco3Wave
            }
        ).onChange(w => { this.synth.setVco3Wave(w); this._saveParams(); });

        this.components.knobs.vco3Fine = themeManager.createKnob(
            document.getElementById('arp_vco3_fine'),
            { min: -100, max: 100, value: params.vco3Fine, label: 'FINE', step: 1, unit: 'ct' }
        ).onChange(v => { this.synth.setVco3Fine(v); this._saveParams(); });

        this.components.knobs.vco3Level = themeManager.createKnob(
            document.getElementById('arp_vco3_level'),
            { min: 0, max: 1, value: params.vco3Level, label: 'LEVEL', step: 0.01 }
        ).onChange(v => { this.synth.setVco3Level(v); this._saveParams(); });

        // Noise & Ring Mod
        this.components.knobs.noiseLevel = themeManager.createKnob(
            document.getElementById('arp_noise_level'),
            { min: 0, max: 1, value: params.noiseLevel, label: 'NOISE', step: 0.01 }
        ).onChange(v => { this.synth.setNoiseLevel(v); this._saveParams(); });

        this.components.knobs.ringLevel = themeManager.createKnob(
            document.getElementById('arp_ring_level'),
            { min: 0, max: 1, value: params.ringModLevel, label: 'RING', step: 0.01 }
        ).onChange(v => { this.synth.setRingModLevel(v); this._saveParams(); });

        // VCF
        this.components.knobs.filterCutoff = themeManager.createKnob(
            document.getElementById('arp_filter_cutoff'),
            { min: 20, max: 10000, value: params.filterCutoff, label: 'FREQ', step: 1 }
        ).onChange(v => { this.synth.setFilterCutoff(v); this._saveParams(); });

        this.components.knobs.filterRes = themeManager.createKnob(
            document.getElementById('arp_filter_res'),
            { min: 0, max: 20, value: params.filterResonance, label: 'RES', step: 0.1 }
        ).onChange(v => { this.synth.setFilterResonance(v); this._saveParams(); });

        this.components.knobs.filterEnv = themeManager.createKnob(
            document.getElementById('arp_filter_env'),
            { min: 0, max: 1, value: params.filterEnvAmount, label: 'ENV', step: 0.01 }
        ).onChange(v => { this.synth.setFilterEnvAmount(v); this._saveParams(); });

        // ENV 1 (Filter)
        this.components.knobs.env1A = themeManager.createKnob(
            document.getElementById('arp_env1_a'),
            { min: 0.001, max: 2, value: params.env1Attack, label: 'A', step: 0.001 }
        ).onChange(v => { this.synth.setEnv1Attack(v); this._saveParams(); });

        this.components.knobs.env1D = themeManager.createKnob(
            document.getElementById('arp_env1_d'),
            { min: 0.001, max: 2, value: params.env1Decay, label: 'D', step: 0.001 }
        ).onChange(v => { this.synth.setEnv1Decay(v); this._saveParams(); });

        this.components.knobs.env1S = themeManager.createKnob(
            document.getElementById('arp_env1_s'),
            { min: 0, max: 1, value: params.env1Sustain, label: 'S', step: 0.01 }
        ).onChange(v => { this.synth.setEnv1Sustain(v); this._saveParams(); });

        this.components.knobs.env1R = themeManager.createKnob(
            document.getElementById('arp_env1_r'),
            { min: 0.001, max: 3, value: params.env1Release, label: 'R', step: 0.001 }
        ).onChange(v => { this.synth.setEnv1Release(v); this._saveParams(); });

        // ENV 2 (VCA)
        this.components.knobs.env2A = themeManager.createKnob(
            document.getElementById('arp_env2_a'),
            { min: 0.001, max: 2, value: params.env2Attack, label: 'A', step: 0.001 }
        ).onChange(v => { this.synth.setEnv2Attack(v); this._saveParams(); });

        this.components.knobs.env2D = themeManager.createKnob(
            document.getElementById('arp_env2_d'),
            { min: 0.001, max: 2, value: params.env2Decay, label: 'D', step: 0.001 }
        ).onChange(v => { this.synth.setEnv2Decay(v); this._saveParams(); });

        this.components.knobs.env2S = themeManager.createKnob(
            document.getElementById('arp_env2_s'),
            { min: 0, max: 1, value: params.env2Sustain, label: 'S', step: 0.01 }
        ).onChange(v => { this.synth.setEnv2Sustain(v); this._saveParams(); });

        this.components.knobs.env2R = themeManager.createKnob(
            document.getElementById('arp_env2_r'),
            { min: 0.001, max: 3, value: params.env2Release, label: 'R', step: 0.001 }
        ).onChange(v => { this.synth.setEnv2Release(v); this._saveParams(); });

        // LFO
        this.components.waveSelectors.lfo = new WaveSelector(
            document.getElementById('arp_lfo_wave'),
            {
                waveforms: [
                    { value: 'triangle', label: 'TRI' },
                    { value: 'sine', label: 'SIN' },
                    { value: 'square', label: 'SQR' },
                    { value: 'sawtooth', label: 'SAW' }
                ],
                value: params.lfoWave
            }
        ).onChange(w => { this.synth.setLfoWave(w); this._saveParams(); });

        this.components.knobs.lfoRate = themeManager.createKnob(
            document.getElementById('arp_lfo_rate'),
            { min: 0.1, max: 20, value: params.lfoRate, label: 'RATE', step: 0.1, unit: 'Hz' }
        ).onChange(v => { this.synth.setLfoRate(v); this._saveParams(); });

        this.components.knobs.lfoVco = themeManager.createKnob(
            document.getElementById('arp_lfo_vco'),
            { min: 0, max: 1, value: params.lfoToVco, label: '→VCO', step: 0.01 }
        ).onChange(v => { this.synth.setLfoToVco(v); this._saveParams(); });

        this.components.knobs.lfoVcf = themeManager.createKnob(
            document.getElementById('arp_lfo_vcf'),
            { min: 0, max: 1, value: params.lfoToFilter, label: '→VCF', step: 0.01 }
        ).onChange(v => { this.synth.setLfoToFilter(v); this._saveParams(); });

        this.components.knobs.lfoVca = themeManager.createKnob(
            document.getElementById('arp_lfo_vca'),
            { min: 0, max: 1, value: params.lfoToVca, label: '→VCA', step: 0.01 }
        ).onChange(v => { this.synth.setLfoToVca(v); this._saveParams(); });

        // Master
        this.components.knobs.volume = themeManager.createKnob(
            document.getElementById('arp_volume'),
            { min: 0, max: 1, value: params.volume, label: 'VOL', step: 0.01 }
        ).onChange(v => { this.synth.setVolume(v); this._saveParams(); });

        this.components.knobs.glide = themeManager.createKnob(
            document.getElementById('arp_glide'),
            { min: 0, max: 0.5, value: params.portamento, label: 'GLIDE', step: 0.01 }
        ).onChange(v => { this.synth.setPortamento(v); this._saveParams(); });

        this.setupPresetSelector();
    }

    setupPresetSelector() {
        const container = document.getElementById('arp2600_presets');
        if (!container) return;

        this.presetSelector = new PresetSelector({
            presets: ARP2600_PRESETS,
            currentPreset: 'Init',
            onChange: (name, data) => this._loadPreset(data)
        });

        container.appendChild(this.presetSelector.element);
    }

    createSequencer() {
        const notesContainer = document.getElementById('arp2600_notes');
        const gatesContainer = document.getElementById('arp2600_gates');

        // Note selectors
        for (let i = 0; i < 16; i++) {
            this.components.selects[`note_${i}`] = themeManager.createSelect(notesContainer, {
                options: this.store.noteOptions,
                value: this.store.patterns.arp2600Notes ? this.store.patterns.arp2600Notes[i] : 'C3',
                className: 'note-select'
            }).onChange(value => {
                this.store.setStep('arp2600Notes', i, value);
            });
        }

        // Gate buttons
        for (let i = 0; i < 16; i++) {
            const stepBtn = themeManager.createStepButton(gatesContainer, {
                index: i,
                type: 'gate',
                active: this.store.patterns.arp2600Gates ? this.store.patterns.arp2600Gates[i] : false
            }).onChange(active => {
                this.store.setStep('arp2600Gates', i, active);
            });

            this.components.stepButtons.push(stepBtn);
        }
    }

    highlightStep(step) {
        this.components.stepButtons.forEach((btn, i) => {
            if (btn && btn.setPlaying) {
                btn.setPlaying(i === step);
            }
        });
    }

    _loadPreset(presetData) {
        if (!presetData) return;

        this.synth.setParams(presetData);
        this._updateKnobsFromParams(presetData);
        this._saveParams();
    }

    _saveParams() {
        this.store?.setSynthParams('arp2600', this.synth.getParams());
    }

    _updateKnobsFromParams(params) {
        // Update knobs
        if (this.components.knobs.vco1Fine) this.components.knobs.vco1Fine.setValue(params.vco1Fine);
        if (this.components.knobs.vco1Level) this.components.knobs.vco1Level.setValue(params.vco1Level);
        if (this.components.knobs.vco2Fine) this.components.knobs.vco2Fine.setValue(params.vco2Fine);
        if (this.components.knobs.vco2Level) this.components.knobs.vco2Level.setValue(params.vco2Level);
        if (this.components.knobs.vco3Fine) this.components.knobs.vco3Fine.setValue(params.vco3Fine);
        if (this.components.knobs.vco3Level) this.components.knobs.vco3Level.setValue(params.vco3Level);
        if (this.components.knobs.noiseLevel) this.components.knobs.noiseLevel.setValue(params.noiseLevel);
        if (this.components.knobs.ringLevel) this.components.knobs.ringLevel.setValue(params.ringModLevel);
        if (this.components.knobs.filterCutoff) this.components.knobs.filterCutoff.setValue(params.filterCutoff);
        if (this.components.knobs.filterRes) this.components.knobs.filterRes.setValue(params.filterResonance);
        if (this.components.knobs.filterEnv) this.components.knobs.filterEnv.setValue(params.filterEnvAmount);
        if (this.components.knobs.env1A) this.components.knobs.env1A.setValue(params.env1Attack);
        if (this.components.knobs.env1D) this.components.knobs.env1D.setValue(params.env1Decay);
        if (this.components.knobs.env1S) this.components.knobs.env1S.setValue(params.env1Sustain);
        if (this.components.knobs.env1R) this.components.knobs.env1R.setValue(params.env1Release);
        if (this.components.knobs.env2A) this.components.knobs.env2A.setValue(params.env2Attack);
        if (this.components.knobs.env2D) this.components.knobs.env2D.setValue(params.env2Decay);
        if (this.components.knobs.env2S) this.components.knobs.env2S.setValue(params.env2Sustain);
        if (this.components.knobs.env2R) this.components.knobs.env2R.setValue(params.env2Release);
        if (this.components.knobs.lfoRate) this.components.knobs.lfoRate.setValue(params.lfoRate);
        if (this.components.knobs.lfoVco) this.components.knobs.lfoVco.setValue(params.lfoToVco);
        if (this.components.knobs.lfoVcf) this.components.knobs.lfoVcf.setValue(params.lfoToFilter);
        if (this.components.knobs.lfoVca) this.components.knobs.lfoVca.setValue(params.lfoToVca);
        if (this.components.knobs.volume) this.components.knobs.volume.setValue(params.volume);
        if (this.components.knobs.glide) this.components.knobs.glide.setValue(params.portamento);

        // Update wave selectors
        if (this.components.waveSelectors.vco1) this.components.waveSelectors.vco1.setValue(params.vco1Wave);
        if (this.components.waveSelectors.vco2) this.components.waveSelectors.vco2.setValue(params.vco2Wave);
        if (this.components.waveSelectors.vco3) this.components.waveSelectors.vco3.setValue(params.vco3Wave);
        if (this.components.waveSelectors.lfo) this.components.waveSelectors.lfo.setValue(params.lfoWave);
    }

    destroy() {
        Object.values(this.components.knobs).forEach(k => k?.destroy?.());
        Object.values(this.components.selects).forEach(s => s?.destroy?.());
        Object.values(this.components.waveSelectors).forEach(w => w?.destroy?.());
        this.components.stepButtons.forEach(b => b?.destroy?.());
    }
}
